import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray } from "react-hook-form";
import Iconify from "../../../components/iconify";
import { surveySelectItems, isChoiceType } from "../../../utils/surveyUtils";

// ---- Options sub-form for choice-type questions ----

function QuestionOptionsField({ control, questionIndex, register }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `surveyQuestions.${questionIndex}.options`,
  });

  return (
    <Paper sx={{ p: 2, mt: 1 }} elevation={1}>
      <Typography variant="subtitle2" gutterBottom>
        Antwortoptionen:
      </Typography>
      {fields.map((option, optIndex) => (
        <Grid
          container
          spacing={1}
          key={option.id}
          sx={{ mb: 1 }}
          alignItems="center"
        >
          <Grid item xs={10}>
            <TextField
              {...register(
                `surveyQuestions.${questionIndex}.options.${optIndex}.label`,
                { required: "Option darf nicht leer sein" }
              )}
              label={`Option ${optIndex + 1}`}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => remove(optIndex)}
              disabled={fields.length <= 2}
            >
              <Iconify icon="mdi:delete" />
            </Button>
          </Grid>
        </Grid>
      ))}
      <Button
        variant="outlined"
        size="small"
        onClick={() => append({ label: "" })}
        startIcon={<Iconify icon="tabler:plus" />}
      >
        Option hinzufügen
      </Button>
    </Paper>
  );
}

QuestionOptionsField.propTypes = {
  control: PropTypes.object.isRequired,
  questionIndex: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
};

// ---- Main component ----

SurveyFormQuestionItems.propTypes = {
  fields: PropTypes.array.isRequired,
  register: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
  watchQuestions: PropTypes.array.isRequired,
  control: PropTypes.object,
  setValue: PropTypes.func,
};

function SurveyFormQuestionItems({
  fields,
  remove,
  register,
  watchQuestions,
  control,
  setValue,
}) {
  const handleTypeChange = (field, e, index) => {
    field.onChange(e);
    const newType = e.target.value;
    // Initialize options array when switching to a choice type
    if (isChoiceType(newType)) {
      const current = watchQuestions[index]?.options;
      if (!Array.isArray(current) || current.length < 2) {
        setValue(`surveyQuestions.${index}.options`, [
          { label: "" },
          { label: "" },
        ]);
      }
    }
  };

  return fields.map((surveyQuestions, index) => (
    <React.Fragment key={surveyQuestions.id}>
      {/* Question Input */}
      <Grid item xs={6} lg={6}>
        <TextField
          {...register(`surveyQuestions.${index}.question`, {
            required: "Frage ist erforderlich",
          })}
          label={`Frage ${index + 1}`}
          fullWidth
          multiline
          required
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>

      {/* Question Type Select */}
      <Grid item xs={5} lg={5}>
        <Controller
          control={control}
          name={`surveyQuestions.${index}.type`}
          rules={{ required: "Bitte Art auswählen." }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              select
              fullWidth
              variant="outlined"
              {...field}
              onChange={(e) => handleTypeChange(field, e, index)}
              label="Antwortmöglichkeiten:"
              error={!!error}
              helperText={error?.message}
            >
              <MenuItem value="" disabled>
                Art der Bewertung auswählen
              </MenuItem>
              {surveySelectItems.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      {/* Remove Question Button */}
      <Grid item xs={1} lg={1}>
        <Button variant="contained" onClick={() => remove(index)} color="error">
          <Iconify
            icon="mdi:delete"
            sx={{
              m: 1,
              height: "25px",
              width: "25px",
            }}
          />
        </Button>
      </Grid>

      {/* Choice Options Editor */}
      {isChoiceType(watchQuestions[index]?.type) && (
        <Grid item xs={12}>
          <QuestionOptionsField
            control={control}
            questionIndex={index}
            register={register}
          />
        </Grid>
      )}
    </React.Fragment>
  ));
}

export default SurveyFormQuestionItems;
